import React from 'react';
import { psychologistInfo } from '../mock/mockData';
import { Award, BookOpen, Heart, Sparkles } from 'lucide-react';

const AboutSection = () => {
  const qualifications = [
    {
      icon: BookOpen,
      title: "Formação Académica",
      description: "Licenciatura em Psicologia e Mestrado em Psicologia dos RH, do Trabalho e das Organizações pela Faculdade de Psicologia da Universidade de Lisboa"
    },
    {
      icon: Award,
      title: "Membro da OPP",
      description: "Membro efetivo da Ordem dos Psicólogos Portugueses, garantindo os mais altos padrões de ética e qualidade profissional"
    },
    {
      icon: Sparkles,
      title: "Hipnoterapia Clínica",
      description: "Formação em Hipnoterapia Clínica pela Transpersonal International (2023)"
    },
    {
      icon: Heart,
      title: "Especializações Avançadas",
      description: "Especialização Avançada em Psicoterapia pelo ISPA (2024) e Especialização em Psicologia Clínica e da Saúde no CRIAP (2026)"
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-white to-beige-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-rose-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold-100 rounded-full blur-3xl opacity-20"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-rose-900 mb-4">
            Sobre Mim
          </h2>
          <p className="text-lg text-gray-600">
            Conheça a minha formação e percurso profissional dedicado ao bem-estar mental.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Image Side */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-gold-200 rounded-3xl transform rotate-3"></div>
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={psychologistInfo.aboutImage}
                    alt="Joana Trigo Pessoa - Psicóloga Clínica"
                    className="w-full h-auto object-cover"
                  />
                </div>
                {/* Decorative badge */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-rose-500 to-rose-600 text-white rounded-2xl px-6 py-4 shadow-xl">
                  <p className="text-sm font-semibold">Psicóloga Clínica</p>
                  <p className="text-2xl font-bold">Certificada</p>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-100 to-gold-100 rounded-full mb-4">
                <Award className="h-4 w-4 text-rose-600" />
                <span className="text-sm font-semibold text-rose-800">Membro da OPP</span>
              </div>
              
              <h3 className="text-3xl font-bold text-rose-800 mb-6">
                {psychologistInfo.name}
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Sou <strong className="text-rose-700">Psicóloga Clínica</strong> com formação especializada 
                  em psicoterapia e hipnoterapia clínica, dedicada a ajudar jovens adultos e adultos 
                  a alcançarem o seu bem-estar emocional e mental.
                </p>
                <p>
                  {psychologistInfo.bio}
                </p>
                <p>
                  A minha abordagem terapêutica é <strong className="text-rose-700">personalizada e empática</strong>, 
                  criando um espaço seguro onde pode explorar os seus pensamentos e emoções de forma 
                  genuína e sem julgamentos.
                </p>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => window.open(psychologistInfo.bookingLink, '_blank')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  Agende a Sua Consulta
                </button>
              </div>
            </div>
          </div>

          {/* Qualifications Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {qualifications.map((qual, index) => {
              const IconComponent = qual.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-300 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-100 to-gold-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-6 w-6 text-rose-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-rose-800 mb-2">
                        {qual.title}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {qual.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
